import { WorkDto, WorkExternalPartContactDto, WorkExternalPartDto } from 'api/works/models/WorkDto';
import { BudgetDto } from 'api/budgets/models/BudgetDto';
import WorksService from 'api/works/WorksService';
import CountriesService from 'api/countries/CountriesService';
import LanguagesService from 'api/languages/LanguagesService';
import Logger from 'common/services/Logger';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import Utils from 'common/services/Utils';
import { LOGGER_LOG_TYPE } from 'Config';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Col, OverlayTrigger, Row, Tab, Tabs, Tooltip } from 'react-bootstrap';
import { FaExternalLinkAlt, FaSyncAlt } from 'react-icons/fa';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import InfoMessage from 'common/components/infoMessage/InfoMessage';
import { Label } from 'common/components/texts/Texts';
import { TextInputController } from 'common/components/textInput/TextInput';
import { SelectInputController, SelectInputOption } from 'common/components/selectInput/SelectInput';
import { PhoneCodeOption } from 'common/components/phoneInput/PhoneInput';
import WorkExternalParts from 'screens/business/works/work/WorkExternalParts';
import WorkExternalPartsContactsList from 'screens/business/works/work/contacts/WorkExternalPartsContactsList';

type WorkTabKey = 'details' | 'contacts';

function BudgetWorkTab(): JSX.Element | null {
    const { t } = useTranslation();
    const budgetForm = useFormContext<BudgetDto>();
    const workId = useWatch({ control: budgetForm.control, name: 'workId' });

    const workForm = useForm<WorkDto>({ shouldUnregister: false });

    const [loaded, setLoaded] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState<WorkTabKey>('details');
    const [countries, setCountries] = useState<SelectInputOption[]>([]);
    const [phoneCodesOptions, setPhoneCodesOptions] = useState<PhoneCodeOption[]>([]);
    const [languages, setLanguages] = useState<SelectInputOption[]>([]);
    const [externalPartsSelected, setExternalPartsSelected] = useState<WorkExternalPartDto[]>([]);
    const [contactsList, setContactsList] = useState<WorkExternalPartContactDto[]>([]);

    const shouldRefreshOnFocus = useRef<boolean>(false);

    const loadWork = useCallback(async () => {
        if (!workId) {
            setLoaded(false);
            return;
        }

        try {
            Loading.show();

            const [workData, countriesData, languagesData] = await Promise.all([
                WorksService.getById(workId),
                CountriesService.getCatalog(),
                LanguagesService.GetAllForSelectItem(),
            ]);

            setCountries(countriesData
                .map(x => ({
                    label: t(`countries.${x.isoCodeSmall}`, { defaultValue: x.name }),
                    value: x.id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)));

            setPhoneCodesOptions(countriesData
                .filter(x => x.phoneCode)
                .map(p => ({ value: p.id, name: p.isoCodeSmall, phoneCode: p.phoneCode })));

            setLanguages((languagesData ?? []).map(x => ({
                label: (x.fullLabel ?? x.label) ?? '',
                value: x.value ?? '',
            })));

            const externalParts = (workData.externalParts ?? []).filter(x => x.externalPartId);
            setExternalPartsSelected(externalParts);

            const contacts = (workData.externalPartsContacts ?? []).map(c => ({
                ...c,
                key: c.key ?? Utils.newGuid(),
            }));
            setContactsList(contacts);

            workForm.reset({
                ...workData,
                externalPartsContacts: contacts,
            });
            setLoaded(true);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get work data for budget', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }, [workId, t, workForm]);

    useEffect(() => {
        void loadWork();
    }, [loadWork]);

    useEffect(() => {
        const onMaybeVisible = () => {
            if (shouldRefreshOnFocus.current && workId && !document.hidden) {
                shouldRefreshOnFocus.current = false;
                void loadWork();
            }
        };
        document.addEventListener('visibilitychange', onMaybeVisible);
        window.addEventListener('focus', onMaybeVisible);
        return () => {
            document.removeEventListener('visibilitychange', onMaybeVisible);
            window.removeEventListener('focus', onMaybeVisible);
        };
    }, [loadWork, workId]);

    if (!workId) {
        return (
            <InfoMessage type='information' message={t('budgets.budget.work_tab.no_work_selected')} closable={false} />
        );
    }

    if (!loaded) {
        return null;
    }

    const noop = () => { /* read-only mode */ };

    const onSelectTab = (k: string | null) => {
        if (k === 'configure') {
            shouldRefreshOnFocus.current = true;
            window.open('/business/works/edit/' + workId, '_blank');
            return;
        }
        if (k === 'refresh') {
            void loadWork();
            return;
        }
        setCurrentTab(k as WorkTabKey);
    };

    return (
        <FormProvider {...workForm}>
            <Tabs activeKey={currentTab} onSelect={onSelectTab}>
                <Tab eventKey={'details'} title={t('common.details')}>
                    <Row className='mb-3'>
                        <Col xs={12}>
                            <Label space>{t('works.work.name')}</Label>
                            <TextInputController
                                name='name'
                                control={workForm.control}
                                placeholder={t('works.work.name')}
                                disabled
                            />
                        </Col>
                    </Row>
                    <Row className='mb-3'>
                        <Col xs={12}>
                            <Label space>{t('works.work.address')}</Label>
                            <TextInputController
                                name='address'
                                control={workForm.control}
                                placeholder={t('works.work.address')}
                                disabled
                            />
                        </Col>
                    </Row>
                    <Row className='mb-3'>
                        <Col xs={12} md={6}>
                            <Label space>{t('works.work.zip_code')}</Label>
                            <TextInputController
                                name='zipCode'
                                control={workForm.control}
                                placeholder={t('works.work.zip_code')}
                                disabled
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <Label space>{t('works.work.country')}</Label>
                            <SelectInputController
                                control={workForm.control}
                                name='countryId'
                                options={countries || []}
                                disabled
                            />
                        </Col>
                    </Row>
                    <Row className='mb-3'>
                        <Col xs={12} md={6}>
                            <Label space>
                                {t('works.work.language')}
                                <OverlayTrigger placement='top' overlay={<Tooltip>{t('works.work.language_tooltip')}</Tooltip>}>
                                    <span><InfoIcon /></span>
                                </OverlayTrigger>
                            </Label>
                            <SelectInputController
                                control={workForm.control}
                                name='languageId'
                                options={languages || []}
                                disabled
                            />
                        </Col>
                    </Row>

                    <WorkExternalParts
                        type='details'
                        onClickRefreshExternalParts={noop}
                        onExternalPartsChange={noop}
                    />
                </Tab>
                <Tab eventKey={'contacts'} title={t('works.work.contacts')}>
                    <WorkExternalPartsContactsList
                        workContactsList={contactsList}
                        type='details'
                        selectedExternalParts={externalPartsSelected}
                        phoneCodesOptions={phoneCodesOptions}
                        onAddNewContact={noop}
                        onEditContact={noop}
                        onRemoveContact={noop}
                    />
                </Tab>
                <Tab
                    eventKey={'configure'}
                    title={<><FaExternalLinkAlt /> {t('budgets.budget.work_tab.configure_work')}</>}
                />
                <Tab
                    eventKey={'refresh'}
                    title={<span title={t('budgets.budget.work_tab.refresh_work_data')}><FaSyncAlt /></span>}
                />
            </Tabs>
        </FormProvider>
    );
}

export default BudgetWorkTab;
